/***
 *
 *   DATA TABLE STYLES
 *   The table treatment used across ui-reference (contacts-list-view,
 *   team-users-table, ticket-list-view): a bordered surface with a muted head,
 *   hairline row borders, a soft row hover and a top-bordered footer holding
 *   the range + page controls.
 *
 *   The `*Scroll` variants apply only when DataTable is given `scroll`, which
 *   is also what makes the head sticky — see the prop's note in dataTable.js.
 *
 **********/

const Style = {
    // shell
    wrap: 'flex min-w-0 flex-col gap-3',
    /* `scroll` gives the table its own viewport instead of growing the page.
       It buys a sticky head, and costs the ability to overhang the container
       — so a table with a row menu should leave it off (see DataTable). */
    wrapScroll: 'min-h-0 flex-1 basis-0',
    cardsWrap: 'md:hidden',
    cardsWrapScroll: 'min-h-0 flex-1 overflow-y-auto overscroll-y-contain',

    // toolbar
    toolbar: 'flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2',
    toolbarFilters: 'flex min-w-0 flex-1 flex-wrap items-center gap-2',
    toolbarActions: 'flex shrink-0 items-center gap-2',
    search: 'w-full min-w-0 sm:w-60',
    filterField: 'block w-full min-w-0 sm:w-auto',
    filterLabel: 'sr-only',
    select: `h-9 w-full min-w-0 max-w-full rounded-md border border-border bg-surface px-2.5 text-sm
    text-text-primary transition-colors hover:border-text-muted focus-visible:outline-none
    focus-visible:ring-2 focus-visible:ring-ring sm:w-auto`,
    reset: 'h-9 w-full shrink-0 gap-1.5 px-3 sm:w-auto',

    // desktop table
    tableWrap: 'hidden overflow-x-auto rounded-lg border border-border md:block',
    tableWrapScroll: 'min-h-0 flex-1 overflow-y-auto overscroll-y-contain',
    table: 'w-full min-w-full text-sm',
    thead: 'bg-muted/60',
    theadSticky: 'sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/70',
    theadRow: 'border-b border-border',
    th: 'whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary',
    thRight: 'text-right',
    thCenter: 'text-center',
    sortButton: `-mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 align-middle transition-colors
    hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`,
    sortIcon: 'shrink-0 opacity-50',
    sortIconActive: 'shrink-0 opacity-90',

    row: 'border-b border-border transition-colors last:border-b-0 hover:bg-muted/40',
    rowClickable: `cursor-pointer hover:bg-primary-50 focus-visible:bg-primary-50
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring
    dark:hover:bg-primary-900/20 dark:focus-visible:bg-primary-900/20`,
    cell: 'px-3 py-2.5 align-middle text-text-primary',
    cellRight: 'text-right',
    cellCenter: 'text-center',
    cellActions: 'w-px whitespace-nowrap px-2 py-1.5 text-right',

    // empty / loading
    emptyCell: 'px-3 py-10 text-center',
    emptyTitle: 'text-sm font-medium text-text-primary',
    emptyText: 'mt-1 text-sm text-text-secondary',
    emptyAction: 'mt-3 inline-flex',
    loading: 'space-y-2',

    // mobile
    cards: 'space-y-2',
    card: 'w-full rounded-lg border border-border bg-surface p-3 text-left',
    cardClickable: `transition-colors hover:bg-muted/40 focus-visible:outline-none
    focus-visible:ring-2 focus-visible:ring-ring`,
};

export default Style;
